import { buildControllerRules } from "@gettactic/helpers/src/hook-form/buildControllerRules";
import type { DateRange, DateValue } from "@cmp-src/calendar/types";
import { CalendarInput } from "@cmp-src/form/calendar/CalendarInput/CalendarInput";
import type React from "react";
import { Controller, type FieldValues } from "react-hook-form";
import type { HookFormCalendarInputProps } from "./FormCalendarInput.types";

function createSelectHandler(
  mode: "single" | "multiple" | "range" | undefined,
  onChange: (value: unknown, options?: { shouldValidate?: boolean }) => void
) {
  return (date: DateValue | undefined) => {
    if (mode === "single" || mode === undefined) {
      if (date instanceof Date) {
        onChange(new Date(date), { shouldValidate: false });
      } else {
        onChange(undefined, { shouldValidate: false });
      }
    } else if (mode === "multiple") {
      if (Array.isArray(date)) {
        onChange(
          date.map((d) => new Date(d)),
          { shouldValidate: false }
        );
      } else {
        onChange([], { shouldValidate: false });
      }
    } else if (mode === "range") {
      if (date && !Array.isArray(date) && !(date instanceof Date)) {
        const range = date as DateRange;
        onChange(
          {
            from: range.from ? new Date(range.from) : undefined,
            to: range.to ? new Date(range.to) : undefined
          },
          { shouldValidate: false }
        );
      } else {
        onChange(undefined, { shouldValidate: false });
      }
    }
  };
}

function getSelectedDate(
  mode: "single" | "multiple" | "range" | undefined,
  value: unknown
): DateValue | undefined {
  if (mode === "single" || mode === undefined) {
    return value instanceof Date ? value : undefined;
  } else if (mode === "multiple") {
    return Array.isArray(value) ? value : [];
  } else if (mode === "range") {
    return value as DateRange | undefined;
  }
  return undefined;
}

/**
 * `CalendarInput` bound to a react-hook-form field holding a `Date` (single), `Date[]`
 * (multiple) or `{ from, to }` (range). Confirms with Apply unless `setValueOnApply` is false.
 */
export const HookFormCalendarInput = <T extends FieldValues>({
  form,
  name,
  rules,
  ...props
}: HookFormCalendarInputProps<T>): React.ReactElement => {
  const mode = props.mode ?? "single";

  return (
    <Controller
      control={form.control}
      name={name}
      rules={buildControllerRules({
        form,
        name,
        rules,
        isRequired: props.isRequired
      })}
      render={({ field, fieldState }) => {
        const handleSelect = createSelectHandler(mode, field.onChange);
        const selectedDate = getSelectedDate(mode, field.value);

        return (
          <CalendarInput
            {...props}
            mode={mode}
            selectedDate={selectedDate}
            onSelect={handleSelect}
            error={fieldState.error?.message}
            setValueOnApply={props.setValueOnApply ?? true}
          />
        );
      }}
    />
  );
};
